import { toTitleCase } from '@/lib/input-mod'
import { productSkuNames, getDefaultPlan } from '@/lib/plans'
import type { Vertical } from '@/types/verticals'
import type { MembershipLevel } from '@/types/subscriptions'
import type { ProductSku } from '@/types/purchasing'

/**
 * Get the plan object of a product
 *
 * @param productSku - the product's sku
 * @return the plan for the product as defined in plans.ts
 */
export function getPlan(productSku: ProductSku) {
  return getDefaultPlan(productSku)
}

/**
 * Get the app for a given product
 *
 * @param productSku - the product's sku
 * @return app - The app name associated w/ the product or an empty string if the plan wasn't found.
 */
export function getProductApp(productSku: ProductSku): Vertical | string {
  for (const vertical in productSkuNames) {
    for (const product of productSkuNames[vertical as Vertical]) {
      if (productSku === product.humanSku || productSku === product.sku) {
        return vertical
      }
    }
  }
  return ''
}

/**
 * Get the membership level for a product
 *
 * @param productSku - the product's sku
 * @return the membership level for the product
 */
export function getMembershipLevel(productSku: string) {
  const product = getDefaultPlan(productSku)
  return product ? product.level.toLowerCase() : 'basic'
}

/**
 * Get the title of the page based on the app and membershiplevel
 *
 * @param app - The app name (e.g., 'hunt', 'offroad', 'fish').
 * @param productSku - the product's sku
 * @return the title for the purchase page
 */
export function getTitle(
  app: Vertical,
  membershipLevel: MembershipLevel,
  productSku: string
): string {
  if ((app === 'hunt' || app === 'offroad') && productSku) {
    const productMembershipLevel = getMembershipLevel(productSku)

    return `Set Up ${toTitleCase(productMembershipLevel)} Membership`
  }

  if (membershipLevel) {
    return `Set Up ${toTitleCase(membershipLevel)} Membership`
  }

  return `Set Up onX ${toTitleCase(app)} Membership`
}
